import React, { useEffect, useRef, useState } from 'react';
import { Button, Dropdown, Icon } from 'semantic-ui-react';
import { API } from '../../helpers';
import './dashboardScopeV112.css';

export const dashboardStatisticsURLV112 = (period, filter) => {
  const query = new URLSearchParams({ period });
  if (filter.scope !== 'all') query.set('customer_scope', filter.scope);
  if (filter.account) query.set('billing_account_id', filter.account);
  return '/api/dashboard/statistics?' + query.toString();
};

export const dashboardScopeActiveV112 = (filter) =>
  filter.scope !== 'all' || Boolean(filter.account);

export default function DashboardScopeV112({ filter, onChange, t }) {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  const [retry, setRetry] = useState(0);
  const request = useRef(0);
  const label = (key) => t('dashboard_scope_v112.' + key);
  useEffect(() => {
    const generation = ++request.current;
    const abort = new AbortController();
    setLoading(true);
    setFailed(false);
    setOptions([]);
    API.get('/api/dashboard/organizations?customer_scope=' + filter.scope, {
      signal: abort.signal,
    })
      .then((response) => {
        if (generation !== request.current || abort.signal.aborted) return;
        if (!response?.data?.success || !Array.isArray(response.data.data))
          throw new Error('Organization options unavailable');
        setOptions(
          response.data.data.map((item) => ({
            key: String(item.id),
            value: String(item.id),
            text: (item.name || '#' + item.id) + ' (#' + item.id + ')',
          }))
        );
      })
      .catch(() => {
        if (generation === request.current && !abort.signal.aborted)
          setFailed(true);
      })
      .finally(() => {
        if (generation === request.current && !abort.signal.aborted)
          setLoading(false);
      });
    return () => abort.abort();
  }, [filter.scope, retry]);

  const organizationOptions = [
    { key: 'all', value: '', text: label('all_organizations') },
    ...options,
  ];
  if (filter.account && !options.some((item) => item.value === filter.account))
    organizationOptions.push({
      key: filter.account,
      value: filter.account,
      text: filter.name || '#' + filter.account,
    });

  return (
    <section className='dashboard-filters-v112' aria-label={label('filters')}>
      <div className='dashboard-filter-v112'>
        <label id='dashboard-scope-label-v112'>{label('customer_scope')}</label>
        <Dropdown
          selection
          name='customer_scope'
          aria-labelledby='dashboard-scope-label-v112'
          value={filter.scope}
          options={['all', 'ordinary', 'special'].map((value) => ({
            key: value,
            value,
            text: label(value),
          }))}
          onChange={(_, { value }) =>
            onChange({ scope: value, account: '', name: '' })
          }
        />
      </div>
      <div className='dashboard-filter-v112 dashboard-organization-v112'>
        <label id='dashboard-organization-label-v112'>
          {label('organization')}
        </label>
        <Dropdown
          selection
          search
          name='billing_account_id'
          aria-labelledby='dashboard-organization-label-v112'
          value={filter.account}
          options={organizationOptions}
          loading={loading}
          disabled={loading || failed}
          noResultsMessage={label('no_organizations')}
          onChange={(_, { value }) =>
            onChange({
              ...filter,
              account: value,
              name: options.find((item) => item.value === value)?.text || '',
            })
          }
        />
      </div>
      {dashboardScopeActiveV112(filter) ? (
        <Button
          basic
          icon
          size='small'
          aria-label={label('clear')}
          title={label('clear')}
          onClick={() => onChange({ scope: 'all', account: '', name: '' })}
        >
          <Icon name='close' />
        </Button>
      ) : null}
      {failed ? (
        <div className='dashboard-filter-error-v112' role='alert'>
          <span>{label('load_failed')}</span>
          <Button
            basic
            icon
            size='small'
            title={label('retry')}
            aria-label={label('retry')}
            onClick={() => setRetry((value) => value + 1)}
          >
            <Icon name='refresh' />
          </Button>
        </div>
      ) : null}
    </section>
  );
}
